import { ReactNode } from 'react';
import MuiModal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import styled from '@emotion/styled';

import { ModalShell, ModalShellSize } from './ModalShell';
import { ModalCloseButton } from './ModalCloseButton';

type ModalProps = {
    open: boolean;
    onClose: () => void;
    children: ReactNode;
    size?: ModalShellSize;
    ariaLabel?: string;
    hideCloseButton?: boolean;
    /** `start` top-aligns the shell below the app header; `center` keeps vertical centering. */
    backdropAlign?: 'center' | 'start';
    /** Use a higher value when stacking a modal on top of another (default 1300). */
    zIndex?: number;
};

const Backdrop = styled.div<{ $align: 'center' | 'start' }>`
    position: fixed;
    inset: 0;
    display: flex;
    align-items: ${({ $align }) => ($align === 'start' ? 'flex-start' : 'safe center')};
    justify-content: center;
    padding: 16px;
    padding-top: ${({ $align }) => ($align === 'start' ? 'clamp(72px, 11vh, 112px)' : '16px')};
    background: rgba(15, 23, 42, 0.35);
    overflow-y: auto;
`;

const ShellBody = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    /* stretch scroll region to the shell edges so the scrollbar sits flush with
       the rounded corners, while preserving the shell's padding for content. */
    margin: -32px -24px -24px;
    padding: 32px 24px 24px;

    @media (max-width: 640px) {
        margin: -24px -18px -18px;
        padding: 24px 18px 18px;
    }

    /* Lightweight scrollbar styling so long modals don't look heavy. */
    scrollbar-width: thin;
    scrollbar-color: rgba(148, 163, 184, 0.45) transparent;

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgba(148, 163, 184, 0.45);
        border-radius: 999px;
        border: 2px solid transparent;
        background-clip: padding-box;
    }

    &::-webkit-scrollbar-thumb:hover {
        background-color: rgba(131, 83, 226, 0.55);
    }
`;

export const Modal = ({
    open,
    onClose,
    children,
    size = 'md',
    ariaLabel,
    hideCloseButton = false,
    backdropAlign = 'center',
    zIndex = 1300,
}: ModalProps) => (
    <MuiModal
        open={open}
        onClose={onClose}
        closeAfterTransition
        hideBackdrop
        aria-label={ariaLabel}
        sx={{ zIndex }}
    >
        <Fade in={open}>
            <Backdrop
                $align={backdropAlign}
                onMouseDown={(event) => {
                    if (event.target === event.currentTarget) onClose();
                }}
            >
                <ModalShell size={size}>
                    {!hideCloseButton && <ModalCloseButton onClick={onClose} />}
                    <ShellBody>{children}</ShellBody>
                </ModalShell>
            </Backdrop>
        </Fade>
    </MuiModal>
);

export default Modal;
